/**
 * CowLot Collection — AJAX Filtering
 * Lightweight custom element for the cowlot collection template.
 */

(function () {
  'use strict';

  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  class CowlotFilters extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('#CowlotFiltersForm');
      this.sectionId = this.dataset.sectionId;
      this.cache = [];
      this.activeDropdown = null;

      if (!this.form) return;

      // Debounced handler for price inputs
      this.debouncedSubmit = debounce((event) => this.onSubmit(event), 500);

      // Filter & sort changes
      this.form.addEventListener('input', (event) => {
        if (event.target.type === 'number') {
          this.debouncedSubmit(event);
        } else {
          this.onSubmit(event);
        }
      });

      const sortSelect = this.form.querySelector('#CowlotSortBy');
      if (sortSelect) {
        sortSelect.addEventListener('change', (event) => this.onSubmit(event));
      }

      // Dropdown toggles
      this.setupDropdowns();

      // Browser back/forward
      window.addEventListener('popstate', (event) => {
        const params = event.state?.searchParams || '';
        if (params !== this._lastParams) {
          this.renderPage(params, null, false);
        }
      });

      // Active filter pill removal (event delegation on document)
      document.addEventListener('click', (event) => {
        const pill = event.target.closest('.js-remove-filter');
        if (!pill) return;
        event.preventDefault();
        const url = pill.getAttribute('href');
        const params = url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
        this.renderPage(params);
      });

      this._lastParams = window.location.search.slice(1);
    }

    /* ---- Dropdown Behavior ---- */

    setupDropdowns() {
      // Use event delegation on the form so AJAX-swapped elements still work
      this.form.addEventListener('click', (e) => {
        const toggle = e.target.closest('.cowlot-filter-group__toggle');
        if (!toggle) return;

        e.preventDefault();
        const dropdown = document.getElementById(toggle.getAttribute('aria-controls'));
        if (!dropdown) return;

        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        this.closeAllDropdowns();

        if (!isOpen) {
          toggle.setAttribute('aria-expanded', 'true');
          dropdown.removeAttribute('hidden');
          this.activeDropdown = { toggle, dropdown };

          // On mobile, add backdrop
          if (window.innerWidth < 750) {
            const backdrop = document.createElement('div');
            backdrop.className = 'cowlot-filter-backdrop';
            backdrop.addEventListener('click', () => this.closeAllDropdowns());
            document.body.appendChild(backdrop);
          }
        }
      });

      // Close on click outside (document-level, added once)
      document.addEventListener('click', (event) => {
        if (!this.activeDropdown) return;
        if (event.target.closest('.cowlot-filter-group')) return;
        if (event.target.closest('.cowlot-filter-backdrop')) return;
        this.closeAllDropdowns();
      });

      // Close on Escape
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.activeDropdown) {
          const toggle = this.activeDropdown.toggle;
          this.closeAllDropdowns();
          if (toggle) toggle.focus();
        }
      });
    }

    closeAllDropdowns() {
      this.querySelectorAll('.cowlot-filter-group__toggle').forEach((t) => {
        t.setAttribute('aria-expanded', 'false');
      });
      this.querySelectorAll('.cowlot-filter-group__dropdown').forEach((d) => {
        d.setAttribute('hidden', '');
      });
      this.activeDropdown = null;

      // Remove mobile backdrop
      const backdrop = document.querySelector('.cowlot-filter-backdrop');
      if (backdrop) backdrop.remove();
    }

    /* ---- Form Submission ---- */

    onSubmit(event) {
      const formData = new FormData(this.form);
      const searchParams = new URLSearchParams(formData).toString();
      this.renderPage(searchParams, event);
    }

    /* ---- AJAX Page Render ---- */

    async renderPage(searchParams, event = null, pushState = true) {
      this._lastParams = searchParams;
      this.showLoading();

      const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`;

      let html;
      const cached = this.cache.find((c) => c.url === url);
      if (cached) {
        html = cached.html;
      } else {
        try {
          const response = await fetch(url);
          html = await response.text();
          this.cache.push({ url, html });
        } catch (err) {
          console.error('[CowlotFilters] Fetch error:', err);
          this.hideLoading();
          return;
        }
      }

      const parsed = new DOMParser().parseFromString(html, 'text/html');

      // Update product grid
      const newGrid = parsed.getElementById('CowlotGridContainer');
      const currentGrid = document.getElementById('CowlotGridContainer');
      if (newGrid && currentGrid) {
        currentGrid.innerHTML = newGrid.innerHTML;
      }

      // Update active filter pills
      const newActive = parsed.getElementById('CowlotActiveFilters');
      const currentActive = document.getElementById('CowlotActiveFilters');
      if (newActive && currentActive) {
        currentActive.outerHTML = newActive.outerHTML;
      } else if (newActive && !currentActive) {
        // Active filters appeared — insert before grid
        const grid = document.getElementById('CowlotGridContainer');
        if (grid) grid.insertAdjacentElement('beforebegin', newActive);
      } else if (!newActive && currentActive) {
        currentActive.setAttribute('hidden', '');
      }

      // Update filter groups (skip the one user is interacting with)
      this.updateFilters(parsed, event);

      // Update product count in banner
      const newCount = parsed.getElementById('CowlotProductCount');
      const currentCount = document.getElementById('CowlotProductCount');
      if (newCount && currentCount) {
        currentCount.textContent = newCount.textContent;
      }

      // Push browser history
      if (pushState) {
        const newUrl = searchParams
          ? `${window.location.pathname}?${searchParams}`
          : window.location.pathname;
        history.pushState({ searchParams }, '', newUrl);
      }

      this.hideLoading();
    }

    updateFilters(parsed, event) {
      const interactingId = event?.target?.closest('.js-filter')?.id;
      const newFilters = parsed.querySelectorAll('#CowlotFiltersForm .js-filter');

      newFilters.forEach((newFilter) => {
        if (newFilter.id === interactingId) return;
        const current = document.getElementById(newFilter.id);
        if (current) {
          current.innerHTML = newFilter.innerHTML;
        }
      });
    }

    showLoading() {
      const loading = document.getElementById('CowlotLoading');
      const grid = document.querySelector('.cowlot-grid');
      if (loading) loading.removeAttribute('hidden');
      if (grid) grid.classList.add('cowlot-grid--loading');
    }

    hideLoading() {
      const loading = document.getElementById('CowlotLoading');
      const grid = document.querySelector('.cowlot-grid');
      if (loading) loading.setAttribute('hidden', '');
      if (grid) grid.classList.remove('cowlot-grid--loading');
    }
  }

  customElements.define('cowlot-filters', CowlotFilters);
})();